import React, { type JSX } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ChatbotLayout from "../../ChatbotLayout_Component";
import ResizableDivider from "../../ResizableDivider_Component";
import WorkspaceTabBar, { type WorkspaceTabBarProps } from "./WorkspaceTabBar";
import type { ChatPanelResize } from "../../hooks/useChatPanelResize";
import type { WorkspaceEntrance } from "../../hooks/useWorkspaceEntrance";

/** Chat on its own, centred, before the workspace has anything to show. */
function CenteredChat(): JSX.Element {
  return (
    <div className="flex-1 flex justify-center h-full">
      <div className="w-full max-w-5xl h-full flex flex-col">
        {/* White container - content area */}
        <div className="flex-1 min-h-0 bg-white shadow-xs border border-cvc-grey overflow-hidden flex flex-col mx-4 mt-4">
          <ChatbotLayout chatInputAriaLabel="Desktop message input field" />
        </div>
      </div>
    </div>
  );
}

/** Dismisses the whole workspace panel; only offered behind the inline flag. */
function CloseSidebarButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      onClick={onClick}
      className="shrink-0 ml-auto mr-2 inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-cvcBody text-gray-500 hover:text-cvc-blue hover:bg-gray-100 rounded-sm transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-cvc-blue"
      aria-label="Close sidebar"
      title="Close sidebar"
    >
      <XMarkIcon className="h-4 w-4" />
      <span>Close sidebar</span>
    </button>
  );
}

/** Right-hand workspace column: the tab strip above the active panel. */
function WorkspacePanel({
  opacity,
  isDragging,
  showCloseButton,
  onClosePanel,
  tabs,
  content,
}: {
  opacity: number;
  isDragging: boolean;
  showCloseButton: boolean;
  onClosePanel: () => void;
  tabs: WorkspaceTabBarProps;
  content: JSX.Element | null;
}): JSX.Element {
  return (
    <div
      className={`flex-1 min-w-0 overflow-hidden h-full flex flex-col transition-opacity duration-500 ease-in-out ${isDragging ? "" : "transition-all duration-500 ease-in-out"}`}
      style={{ opacity }}
    >
      <div className="flex items-center shrink-0 border-b border-cvc-grey tablet:sticky tablet:top-0 tablet:z-10 tablet:bg-white">
        {/* Tabs scroll horizontally if they overflow; close button stays anchored. */}
        <div className="flex items-center min-w-0 flex-1 overflow-x-auto">
          <WorkspaceTabBar variant="desktop" tabs={tabs} />
        </div>
        {showCloseButton && <CloseSidebarButton onClick={onClosePanel} />}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden">{content}</div>
    </div>
  );
}

interface DesktopDealsViewProps {
  showWorkspace: boolean;
  resize: ChatPanelResize;
  entrance: WorkspaceEntrance;
  inlineDealsFlag: boolean;
  onClosePanel: () => void;
  tabs: WorkspaceTabBarProps;
  content: JSX.Element | null;
}

/** The wide-viewport layout: centred chat, or chat and workspace side by side. */
export default function DesktopDealsView({
  showWorkspace,
  resize,
  entrance,
  inlineDealsFlag,
  onClosePanel,
  tabs,
  content,
}: DesktopDealsViewProps): JSX.Element {
  return (
    <div className="hidden md:flex h-full">
      {!showWorkspace ? (
        <CenteredChat />
      ) : (
        <div className="flex-1 h-full tablet:p-4 overflow-hidden">
          <div
            ref={resize.splitLayoutRef}
            className="flex h-full overflow-hidden"
            style={
              { "--chat-panel-width": `${resize.chatWidth}%` } as React.CSSProperties
            }
          >
            {/* Left column for ChatbotLayout */}
            <div
              className={`flex-none overflow-hidden h-full ${resize.isDragging ? "" : "transition-all duration-500 ease-in-out"}`}
              style={{
                width:
                  entrance.animationPhase === "scrolling"
                    ? "100%"
                    : "var(--chat-panel-width, 33.33%)",
              }}
            >
              <ChatbotLayout chatInputAriaLabel="Desktop message input field" inWorkspaceMode />
            </div>

            <ResizableDivider
              onDragStart={resize.onDragStart}
              onDragMove={resize.onDragMove}
              onDragEnd={resize.onDragEnd}
            />

            <WorkspacePanel
              opacity={entrance.workspaceOpacity}
              isDragging={resize.isDragging}
              showCloseButton={inlineDealsFlag}
              onClosePanel={onClosePanel}
              tabs={tabs}
              content={content}
            />
          </div>
        </div>
      )}
    </div>
  );
}
